import { Component, Inject, OnInit, ViewChildren } from '@angular/core';
import { DevicesService } from '@nrwl/common-lib';
import { CollectorApiService } from '@nrwl/test-setup';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TrafficResultsServiceService } from './traffic-results-service.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ViewTableComponent } from '@nrwl/view-table';
import { MatTableDataSource } from '@angular/material/table';

interface TrafficData {
  interface: string;
  frames: string;
  excludedFrames: string;
  octets: string;
  excludedOctets: string;
  peakOctets: string;
  [key: string]: any;
}

interface SummaryData {
  periodStartTime: string;
  granularity: string;
  monitorName: string;
  periodStatus: string;
  monitorPeriodEndTime: number;
  monitoredTime: string;
  timeUnsync: string;
  sampleInterval: string;
}

@Component({
  selector: 'nrwl-traffic-results',
  templateUrl: './traffic-results.component.html',
  styleUrls: ['./traffic-results.component.scss']
})
export class TrafficResultsComponent implements OnInit {
  @ViewChildren(ViewTableComponent) child: ViewTableComponent;
  isLoading = true;
  isJmep10gVersionOne = true;
  noDataAvailable = false;
  noDataMessage = '';
  evcTrafficPrefix = ['In', 'Eg'];
  portTrafficPrefix = ['Net', 'Eqp'];
  probeResultList = [];
  probeNameList = [];
  summaryDataSource = new MatTableDataSource<SummaryData>([]);
  showPaginator: BehaviorSubject<boolean>;
  interfaceDataSource: TrafficData[];
  pmDataSource = null;
  showPMReport = false;
  formData: any = {};
  evcNameField = 'Unique EVC ID';
  pmFlowNameField = 'Unique Flow ID';
  jmepId: any;
  pageSize = 5;
  defaultPageSize = 5;
  tableOptions = {
    hideActionsColumn: true,
    defaultSorting: {
      id: 'request',
      start: 'desc'
    }
  };
  pageSizeOptions: number[] = [5, 10, 25, 50];
  taskField = {
    evcTraffic: 'evc-traffic',
    portTraffic: 'port-traffic',
    pmReport: 'SOAM-PM'
  };
  probeType = {
    traffic: 'traffic',
    port: 'port',
    pm: 'pm'
  };
  groupedColumns: string[] = ['interfaceGroup', 'framesGroup', 'octetsGroup', 'microburstGroup'];
  jmepName: any;
  binValues = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 200, 300, 400, 500, 600, 700, 800, 900];
  microburstBins = [
    'bin1',
    'bin2',
    'bin3',
    'bin4',
    'bin5',
    'bin6',
    'bin7',
    'bin8',
    'bin9',
    'bin10',
    'bin11',
    'bin12',
    'bin13',
    'bin14',
    'bin15',
    'bin16',
    'bin17',
    'bin18',
    'bin19'
  ];
  displayedColumns: string[];
  displayedColumns1: string[];
  groupHeaders;

  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    private dialogRef: MatDialogRef<TrafficResultsComponent>,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    @Inject(MAT_DIALOG_DATA) public data: any,
    private deviceService: DevicesService,
    private collectorApiService: CollectorApiService,
    private trafficService: TrafficResultsServiceService
  ) {
    // this.summaryDataSource = new BehaviorSubject<any>(null);
    this.displayedColumns = [
      'interface',
      'frames',
      'excludedFrames', // Frames group columns
      'octets',
      'excludedOctets',
      'peakOctets', // Octets group columns
      ...this.microburstBins // Microburst group columns
    ];

    // Define the group headers with colspans for each group
    this.groupHeaders = [
      { name: 'Interface', colspan: 1 },
      { name: 'Frames', colspan: 2 },
      { name: 'Octets', colspan: 3 },
      { name: 'Microburst', colspan: this.microburstBins.length }
    ];
    this.displayedColumns1 = [
      'periodStartTime',
      'monitorPeriodEndTime',
      'monitoredTime',
      'granularity',
      'sampleInterval',
      'periodStatus',
      'timeUnsync'
    ];
  }

  ngOnInit(): void {
    this.formData = {
      selectedProbe: this.data.configId
    };
    this.getDeviceInventory();
  }

  getDeviceInventory(): void {
    this.deviceService
      .getDevices$(null)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        if (response.data && response.data.length) {
          const jmepName = this.data.endpointName;
          this.jmepName = 'Results for jmep: ' + this.data.endpointName;
          const jmepObj = response.data.find(res => res.name === jmepName);
          if (jmepObj) {
            this.jmepId = jmepObj.extension.capabilities.phy
              ? jmepObj.extension.capabilities.phy['jmep-id']
              : '';
            this.isJmep10gVersionOne = this.trafficService.isJmep10versionOne(
              jmepObj.model.version
            );
            this.trafficService.setJMEPModel(jmepObj);
            if (!this.isJmep10gVersionOne) {
              this.getLatestTrafficReport();
            } else {
              this.showNoDataMessage('No Data found for the Jmep');
            }
          } else {
            this.showNoDataMessage('No jmep found in the inventory');
          }
        } else {
          this.showNoDataMessage('No inventory data found');
        }
      });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getBinTitle(value: any): any {
    const multiplier = this.trafficService.getJMEPModel() === 'JMEP-10G' ? 10 : 1;
    return multiplier * value + '+ Mbps';
  }

  getLatestTrafficReport(): void {
    this.probeResultList = [];
    this.probeNameList = [];
    const options = {
      reportType: 'traffic',
      scheduleId: 'jmep' + this.jmepId
    };
    this.collectorApiService
      .getQueryLastReport(options.scheduleId, options.reportType)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          if (
            response.data &&
            response.data['ietf-lmap-report:report'] &&
            response.data['ietf-lmap-report:report'].date
          ) {
            const time = response.data['ietf-lmap-report:report'].date;
            this.getJMEPTrafficReport(time);
          } else {
            this.getLatestPMReport();
          }
        },
        () => {
          this.getLatestPMReport();
        }
      );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getJMEPTrafficReport(time: any): void {
    const options = {
      reportType: 'traffic',
      scheduleId: 'jmep' + this.jmepId,
      runid: 1,
      fromtime: time,
      maxresults: 10
    };
    this.collectorApiService
      .getQueryReportByFromTime(
        options.scheduleId,
        options.runid,
        options.reportType,
        options.fromtime,
        options.maxresults
      )
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        if (response && response.data) {
          this.getSummaryReport(response.data);
          this.getTrafficReport(response.data);
        }
        this.getLatestPMReport();
      });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getSummaryReport(lmapResults: any): void {
    const evcTraffic = lmapResults.find(
      results =>
        results['ietf-lmap-report:report'].result[0]['task-name'] === this.taskField.evcTraffic
    );
    if (evcTraffic) {
      const lmapReport = evcTraffic['ietf-lmap-report:report'].result[0];
      const evcNameIndex = lmapReport.table[0].column.indexOf(this.evcNameField);
      lmapReport.table[0].row.forEach(records => {
        this.probeResultList.push({
          probeName: records.value[evcNameIndex],
          probeData: [
            {
              type: this.probeType.traffic,
              result: this.mapColumnRow(lmapReport.table[0].column, records.value)
            }
          ]
        });
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getTrafficReport(lmapResults: any): void {
    const portTraffic = lmapResults.find(
      results =>
        results['ietf-lmap-report:report'].result[0]['task-name'] === this.taskField.portTraffic
    );

    if (portTraffic) {
      const lmapReport = portTraffic['ietf-lmap-report:report'].result[0];
      this.probeResultList.push({
        probeName: 'Port',
        probeData: [
          {
            type: this.probeType.port,
            result: this.mapColumnRow(lmapReport.table[0].column, lmapReport.table[0].row[0].value)
          }
        ]
      });
    }
  }

  getLatestPMReport(): void {
    if (this.showPMReport) {
      const options = {
        reportType: 'pm',
        scheduleId: 'jmep' + this.jmepId
      };
      this.collectorApiService
        .getQueryLastReport(options.scheduleId, options.reportType)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(response => {
          if (response && response.data['ietf-lmap-report:report']) {
            this.getPMReport(response.data);
          }
          this.populateJMEPResults();
        });
    } else {
      this.isLoading = false;
      this.populateJMEPResults();
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getPMReport(lmapResults: any): void {
    const hasPMResult =
      lmapResults['ietf-lmap-report:report'].result[0]['task-name'] === this.taskField.pmReport;
    if (hasPMResult) {
      const lmapReport = lmapResults['ietf-lmap-report:report'].result[0];
      const evcNameIndex = lmapReport.table[0].column.indexOf(this.pmFlowNameField);
      lmapReport.table[0].row.forEach(records => {
        const probeResultIndex = this.probeResultList.findIndex(
          probe => probe.probeName === records.value[evcNameIndex]
        );
        if (probeResultIndex !== -1) {
          this.probeResultList[probeResultIndex].probeData.push({
            type: this.probeType.pm,
            result: this.mapColumnRow(lmapReport.table[0].column, records.value)
          });
        } else {
          this.probeResultList.push({
            probeName: records.value[evcNameIndex],
            probeData: [
              {
                type: this.probeType.pm,
                result: this.mapColumnRow(lmapReport.table[0].column, records.value)
              }
            ]
          });
        }
      });
    }
  }

  populateJMEPResults(): void {
    if (this.probeResultList.length) {
      this.probeNameList = this.probeResultList.map(list => list.probeName);
      this.onProbeChange();
    } else {
      this.showNoDataMessage('No results found for selected JMEP');
    }
  }

  showNoDataMessage(message: string): void {
    this.isLoading = false;
    this.noDataAvailable = true;
    this.noDataMessage = message;
  }

  onProbeChange(): void {
    this.clearDataSource();
    let selectedProbe = this.probeResultList.find(
      probe => probe.probeName === this.formData.selectedProbe
    );
    // when nodata available for selected evc show first probe as default one
    if (!selectedProbe) {
      this.formData.selectedProbe = this.probeResultList[0].probeName;
      selectedProbe = this.probeResultList[0];
    }
    selectedProbe.probeData.forEach(probeItem => {
      if (probeItem.type === this.probeType.traffic || probeItem.type === this.probeType.port) {
        this.summaryDataSource = this.trafficService.transformSummaryData(probeItem.result);
        const selectedProbePrefix =
          probeItem.type === this.probeType.traffic
            ? this.evcTrafficPrefix
            : this.portTrafficPrefix;
        const interfaceData = this.trafficService.transformInterfaceData(
          probeItem.result,
          selectedProbePrefix
        );
        this.interfaceDataSource = this.trafficService.formatData(interfaceData);
      } else if (probeItem.type === this.probeType.pm) {
        const pmData = this.trafficService.transformPMData(probeItem.result);
        this.pmDataSource = this.checkPMResultAvailable(pmData) ? pmData : null;
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  checkPMResultAvailable(pmData: any): boolean {
    return Object.values(pmData.data[0]).some(
      columnItem => Array.isArray(columnItem) && columnItem.some(item => item && item.label)
    );
  }

  clearDataSource(): void {
    this.summaryDataSource = null;
    this.interfaceDataSource = null;
    this.pmDataSource = null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  mapColumnRow(columnList, rowList): any {
    const columnValue = {};
    rowList.forEach(function (rowObj, index) {
      columnValue[columnList[index]] = rowObj;
    });
    return [columnValue];
  }

  refreshResults(): void {
    this.isLoading = true;
    if (!this.isJmep10gVersionOne) {
      this.getLatestTrafficReport();
    }
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}
